import { Observable } from 'apollo-link';
import { SubscriptionClient } from 'subscriptions-transport-ws';

// The legacy transport exposes these internals in JS. Keep compatibility logic
// in one adapter and test against the installed library, including timer races.
export class AuthenticatedSubscriptionClient extends SubscriptionClient {
    constructor(uri, { getToken, connectionParams }, webSocketImpl) {
        super(uri, {
            reconnect: true,
            reconnectionAttempts: 10,
            minTimeout: 5000,
            lazy: true,
            inactivityTimeout: 30_000,
            connectionParams
        }, webSocketImpl);
        this.getToken = getToken;
        this.lastAuthToken = getToken() || null;
    }

    request(operation) {
        return new Observable(observer => {
            if (!this.getToken()) { observer.complete(); return; }
            return super.request(operation).subscribe(observer);
        });
    }

    connect() {
        if (!this.getToken()) { this.close(); return; }
        super.connect();
    }

    tryReconnect() {
        if (!this.getToken() || Object.keys(this.operations).length === 0) {
            this.close();
            return;
        }
        super.tryReconnect();
    }

    processReceivedData(message) {
        const hadOperations = Object.keys(this.operations).length > 0;
        super.processReceivedData(message);
        // Upstream arms idle cleanup on unsubscribe, but not when the server
        // completes or rejects the last operation before observer cleanup.
        if (hadOperations && Object.keys(this.operations).length === 0 && this.client) {
            this.clearInactivityTimeout();
            this.setInactivityTimeout();
        }
    }

    close(isForced = true, closedByUser = true) {
        // Upstream clears these only when a socket exists. A pending reconnect
        // can outlive logout while `client` is null, so cancel it explicitly.
        if (isForced) {
            this.clearTryReconnectTimeout();
            this.clearMaxConnectTimeout();
            this.clearCheckConnectionInterval();
        }
        super.close(isForced, closedByUser);
        if (isForced) {
            this.operations = {};
            this.unsentMessagesQueue = [];
            this.reconnecting = false;
            this.closedByUser = closedByUser;
        }
    }

    updateAuthToken(token) {
        const normalized = token || null;
        if (normalized === this.lastAuthToken) return;
        this.lastAuthToken = normalized;
        if (!normalized || Object.keys(this.operations).length === 0) {
            this.close();
            return;
        }
        // A newly refreshed credential deserves a fresh bounded retry budget.
        this.backoff.reset();
        if (this.client) this.close(false, false);
        else this.tryReconnect();
    }
}
